import React, { useCallback, useMemo } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";
import { useTranslation } from "react-i18next";
import {
  useCheckInVisitor,
  useIsAlreadyCheckedIn,
  usePrintBadgeForVisitor,
} from "../../../hooks/visitor/mutation";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { AlertCircle } from "lucide-react";
import {
  API_ERRORS,
  CHECK_IN_OUT_TYPE_KEYS,
  CHECK_IN_OUT_TYPES,
} from "../../../constants/common";
import PrintableBadgeCard from "../../card/PrintableBadgeCard";
import { useReactToPrint, UseReactToPrintOptions } from "react-to-print";

interface CheckInVisitorModalProps {
  appointment: any;
  isLateCheckIn: boolean;
  isPrintBadge: boolean;
  isOpen: boolean;
  onOpenChange: () => void;
  gate?: string;
  isPrintBadgeWithoutPicture?: boolean;
}

interface FormData {
  reason?: string;
}

const CheckInVisitorModalSchema = Yup.object({
  reason: Yup.string(),
});

const CheckInVisitorModal = ({
  appointment,
  isLateCheckIn,
  isPrintBadge,
  isOpen,
  onOpenChange,
  gate,
  isPrintBadgeWithoutPicture = false,
}: CheckInVisitorModalProps) => {
  const location = useLocation();
  const componentRef = React.useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormWithSchema(CheckInVisitorModalSchema);

  const {
    mutateAsync: IsAlreadyCheckedIn,
    isPending: isAlreadyCheckedInLoading,
  } = useIsAlreadyCheckedIn();

  const { mutateAsync: CheckInVisitor, isPending: isCheckInVisitorLoading } =
    useCheckInVisitor();
  const { mutateAsync: PrintBadgeForVisitor } = usePrintBadgeForVisitor();

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const visitorPhoto = useMemo(
    () => appointment?.s_Photo && `data:image/png;base64,${appointment?.s_Photo}`,
    [appointment?.s_Photo]
  );

  const visitorInfo = useMemo(
    () => [
      {
        id: 1,
        titleEn: "Reference Number",
        titleAr: "الرقم المرجعي",
        value: appointment?.s_AppointmentCode,
      },
      {
        id: 2,
        titleEn: "Visitor Name",
        titleAr: "اسم الزائر",
        value: appointment?.s_VisitorName_En,
      },
      {
        id: 3,
        titleEn: "Organization Name",
        titleAr: "الجهة التابع لها",
        value: appointment?.s_Sponsor,
      },
      {
        id: 8,
        titleEn: "Building",
        titleAr: "مبنى",
        value:
          i18n.language === "en"
            ? appointment?.s_BuildingName_En
            : appointment?.s_BuildingName_Ar,
      },
      {
        id: 4,
        titleEn: "Department",
        titleAr: "قسم",
        value:
          i18n.language === "en"
            ? appointment?.s_DepartmentName_En
            : appointment?.s_DepartmentName_Ar,
      },
      {
        id: 5,
        titleEn: "Issue and Expiry Date",
        titleAr: "تاريخ الإصدار والانتهاء",
        value: `${formattedDateTimeWithoutSec(
          appointment?.dt_AppointmentStartTime
        )} - ${formattedDateTimeWithoutSec(
          appointment?.dt_AppointmentEndTime
        )}`,
        styles: "font-bold text-red-600 badge-dates",
      },
      {
        id: 6,
        titleEn: "Host Name",
        titleAr: "اسم المضيف",
        value: appointment?.s_HostName,
        styles: "badge-dates"
      },
      {
        id: 7,
        titleEn: "Host Number",
        titleAr: "رقم المضيف",
        value: appointment?.s_HostMobileNo,
      },
      {
        id: 8,
        titleEn: "Devices",
        titleAr: "الأجهزة",
        value:
          i18n.language === "en"
            ? appointment?.s_AllowedDevices_En
            : appointment?.s_AllowedDevices_Ar,
      },
    ],
    [
      appointment?.dt_AppointmentEndTime,
      appointment?.dt_AppointmentStartTime,
      appointment?.s_AllowedDevices_Ar,
      appointment?.s_AllowedDevices_En,
      appointment?.s_AppointmentCode,
      appointment?.s_BuildingName_Ar,
      appointment?.s_BuildingName_En,
      appointment?.s_DepartmentName_Ar,
      appointment?.s_DepartmentName_En,
      appointment?.s_HostMobileNo,
      appointment?.s_HostName,
      appointment?.s_Sponsor,
      appointment?.s_VisitorName_En,
      i18n.language,
    ]
  );

  const handleCheckIn = useCallback(
    async (checkInBody: {
      N_CheckedInType: CHECK_IN_OUT_TYPES;
      S_Reason?: string | undefined;
      encryptedId: string;
    }) => {
      try {
        await CheckInVisitor(checkInBody);
        toast.success(t("checkedInSuccessfully"));

        setTimeout(() => {
          navigate(`${APP_ROUTES.HOME}`);
        }, 700);
      } catch (error: any) {
        const errorCode = Object.keys(error.errors)[0];

        if (errorCode === API_ERRORS.CANNOT_CHECKIN_OLD_VISIT_REQUEST) {
          toast.error(t("cannotCheckInOldVisitRequest"));
        } else if (
          errorCode === API_ERRORS.CANNOT_CHECKIN_FUTURE_VISIT_REQUEST
        ) {
          toast.error(t("cannotCheckInFutureVisitRequest"));
        } else if (
          errorCode === API_ERRORS.CANNOT_CHECKIN_INVALID_VISIT_LOCATION
        ) {
          toast.error(t("cannotCheckInInvalidVisitLocation"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
        return;
      } finally {
        onOpenChange();
      }
    },
    [CheckInVisitor, navigate, onOpenChange, t]
  );

  const handleOnAfterPrint = useCallback(async () => {
    try {
      const body = {
        encryptedId: appointment?.n_AppointmentID?.toString()
          ? appointment?.n_AppointmentID?.toString()
          : "",
      };

      await PrintBadgeForVisitor(body);
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [PrintBadgeForVisitor, appointment?.n_AppointmentID, t]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    documentTitle: "Visitor Badge",
    onAfterPrint: handleOnAfterPrint,
    removeAfterPrint: true,
  } as UseReactToPrintOptions);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      const body = {
        encryptedId: appointment?.n_AppointmentID?.toString()
          ? appointment?.n_AppointmentID?.toString()
          : "",
      };

      if (type !== CHECK_IN_OUT_TYPE_KEYS.VEHICLE) {
        try {
          const { isCheckedIn } = await IsAlreadyCheckedIn(body);

          if (isCheckedIn) {
            toast.error(t("visitorAlreadyCheckedIn"));
            return;
          }
        } catch (error) {
          toast.error(t("somethingWentWrong"));
          return;
        }
      }

      const checkInBody = {
        ...body,
        ...(isLateCheckIn && formData.reason && { S_Reason: formData.reason }),
        N_CheckedInType:
          type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE
            ? CHECK_IN_OUT_TYPES.VEHICLE
            : CHECK_IN_OUT_TYPES.VMS,
        ...(type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE && { N_GateID: gate }),
      };

      if (!isPrintBadge) {
        handleCheckIn(checkInBody);
      } else {
        handleCheckIn(checkInBody);
        handlePrint();
      }
    },
    [
      appointment?.n_AppointmentID,
      isLateCheckIn,
      type,
      gate,
      isPrintBadge,
      IsAlreadyCheckedIn,
      t,
      handleCheckIn,
      handlePrint,
    ]
  );

  return (
    <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-8 justify-center items-center"
      >
        {isLateCheckIn && (
          <div className="flex items-center gap-2 max-w-max rounded-md px-2 -mb-4">
            <AlertCircle className="w-6 text-red-500" />
            <p className="text-lg uppercase text-red-500 font-semibold">
              {t("visitTimeHasEnded")}
            </p>
          </div>
        )}

        <p className="text-lg md:text-2xl font-medium">
          {t("areYouSureYouWantToCheckInThisVisitor")}
        </p>

        <div className="hidden print:block">
          <PrintableBadgeCard
            ref={componentRef}
            visitorInfo={visitorInfo}
            visitorPhoto={visitorPhoto && !isPrintBadgeWithoutPicture ? visitorPhoto : ""}
            qrCode={appointment?.s_QRCodeValue}
          />
        </div>

        {isLateCheckIn && (
          <div className="w-full">
            <Controller
              name="reason"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("reason")}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  value={value}
                />
              )}
            />
          </div>
        )}

        <div className="flex gap-1 items-center">
          <Button
            isLoading={isAlreadyCheckedInLoading || isCheckInVisitorLoading}
          >
            {t("checkIn")}
          </Button>

          <Button
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            onClick={onOpenChange}
          >
            {t("close")}
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default React.memo(CheckInVisitorModal);
